/**
 * Lightweight client tracker used in addition to PixelTracker.
 *
 * Responsibilities:
 *   - Persist UTM params per session so they're attached to every subsequent
 *     event of the same browsing session (not only the entry page).
 *   - Fire backend events for the granular payment stages
 *     (checkout_initialized, payment_attempted, payment_succeeded, payment_failed)
 *     so the Analytics dashboard can render a precise funnel.
 *
 * No DOM, no React — usable from any handler.
 */
import axios from 'axios';
import { API } from '@/lib/api';

const SESSION_KEY = 'ec_session_id';
const UTM_KEY = 'ec_utm';
const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

export function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY) || '';
    if (!sid) {
      sid = (crypto.randomUUID && crypto.randomUUID()) || (Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return Date.now().toString(36);
  }
}

/** Read UTMs from window.location and persist them. If none in the URL,
 *  return the previously stored ones so we keep attribution across pages. */
export function captureUtm() {
  try {
    const url = new URL(window.location.href);
    const fromUrl = {};
    let any = false;
    for (const k of UTM_FIELDS) {
      const v = url.searchParams.get(k) || '';
      if (v) { fromUrl[k] = v; any = true; }
    }
    if (any) {
      localStorage.setItem(UTM_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/** Detect the customer's traffic source (google / facebook / tiktok / …) from
 *  UTM params, ad click IDs and the referrer. Persisted for the session so the
 *  attribution survives the redirect to the payment page. */
const ATTR_KEY = 'ec_attr';
export function getAttribution() {
  try {
    const url = new URL(window.location.href);
    const q = (k) => url.searchParams.get(k) || '';
    const utm = captureUtm();
    const cur = {
      utm_source: utm.utm_source || '',
      utm_medium: utm.utm_medium || '',
      utm_campaign: utm.utm_campaign || '',
      utm_content: utm.utm_content || '',
      utm_term: utm.utm_term || '',
      fbclid: q('fbclid'),
      gclid: q('gclid'),
      ttclid: q('ttclid'),
      referrer: document.referrer || '',
    };
    const hasSignal = Object.values(cur).some(Boolean);
    let attr = cur;
    if (!hasSignal) {
      try { const s = localStorage.getItem(ATTR_KEY); if (s) attr = JSON.parse(s); } catch { /* noop */ }
    }
    attr.source = detectSource(attr);
    if (hasSignal) { try { localStorage.setItem(ATTR_KEY, JSON.stringify(attr)); } catch { /* noop */ } }
    return attr;
  } catch {
    return { source: 'direct' };
  }
}

function detectSource(a) {
  const u = (a.utm_source || '').toLowerCase();
  if (u) {
    if (/(facebook|fb|meta|instagram|^ig$)/.test(u)) return 'facebook';
    if (/(tiktok|^tt$)/.test(u)) return 'tiktok';
    if (/(google|adwords|gads)/.test(u)) return 'google';
    if (/(bing|microsoft)/.test(u)) return 'bing';
    if (/(snap)/.test(u)) return 'snapchat';
    if (/(pinterest)/.test(u)) return 'pinterest';
    if (/(youtube|yt)/.test(u)) return 'youtube';
    return u;
  }
  if (a.fbclid) return 'facebook';
  if (a.gclid) return 'google';
  if (a.ttclid) return 'tiktok';
  const r = (a.referrer || '').toLowerCase();
  if (!r) return 'direct';
  if (/(facebook|instagram|fb\.|fbcdn)/.test(r)) return 'facebook';
  if (/tiktok/.test(r)) return 'tiktok';
  if (/(google\.)/.test(r)) return 'google';
  if (/bing\./.test(r)) return 'bing';
  if (/youtube/.test(r)) return 'youtube';
  if (/(snapchat)/.test(r)) return 'snapchat';
  if (/pinterest/.test(r)) return 'pinterest';
  return 'referral';
}

/** Fire a backend event. Silent on failure — analytics must never break
 *  the checkout. */
let _variant = '';
/** Set the active A/B template variant ('classic'|'lumen') attached to every
 *  subsequent event of this page load. */
export function setTrackVariant(v) { _variant = v || ''; }

export function trackEvent(type, opts = {}) {
  const {
    storeId, slug = '', paymentIntentId = '', amount = 0, currency = 'eur',
    source = '', failureCode = '', failureMessage = '', metadata = {}, variant = '',
  } = opts;
  if (!storeId || !type) return;
  const utm = captureUtm();
  axios.post(`${API}/public/events`, {
    type,
    store_id: storeId,
    session_id: getSessionId(),
    slug,
    payment_intent_id: paymentIntentId,
    amount: Number(amount) || 0,
    currency,
    metadata,
    source,
    failure_code: failureCode,
    failure_message: failureMessage,
    variant: variant || _variant,
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_term: utm.utm_term || '',
    utm_content: utm.utm_content || '',
  }).catch(() => {});
}
